import { useEffect, useState } from 'react'
import { useThreadContextInternal } from '../context/ThreadProvider'

/**
 * Hook to listen for custom events emitted from a thread via the stream WebSocket.
 * Calls the provided callback whenever an event of the specified type is received.
 *
 * Must be used within a ThreadProvider. Events are emitted from the backend
 * using `emitThreadEvent(flow, 'event-type', data)`.
 *
 * @param type - The custom event type to filter for
 * @param callback - Function to call when an event of this type is received
 *
 * @example
 * ```tsx
 * function GamePreview() {
 *   const [gameHtml, setGameHtml] = useState<string | null>(null)
 *
 *   onThreadEvent('game_built', (data: { success: boolean }) => {
 *     if (data.success) {
 *       fetchGameHtml()
 *     }
 *   })
 *
 *   return <iframe srcDoc={gameHtml} />
 * }
 * ```
 *
 * @example
 * ```tsx
 * function ProgressIndicator() {
 *   const [progress, setProgress] = useState(0)
 *
 *   onThreadEvent('progress', (data: { step: number; total: number }) => {
 *     setProgress(data.step / data.total * 100)
 *   })
 *
 *   return <ProgressBar value={progress} />
 * }
 * ```
 */
export function onThreadEvent<T = unknown>(
  type: string,
  callback: (data: T) => void
): void {
  // useThreadContextInternal throws if not within ThreadProvider
  const { subscribeToEvent } = useThreadContextInternal()

  useEffect(() => {
    // Subscribe to events of the specified type
    const unsubscribe = subscribeToEvent<T>(type, callback)

    // Cleanup subscription on unmount
    return unsubscribe
  }, [subscribeToEvent, type, callback])
}

/**
 * Hook to get the latest event data for a specific event type as React state.
 * Returns the most recent event data, or null if no event has been received.
 *
 * Must be used within a ThreadProvider. Events are emitted from the backend
 * using `emitThreadEvent(flow, 'event-type', data)`.
 *
 * @param type - The custom event type to filter for
 * @returns The latest event data matching the specified type, or null if none received
 *
 * @example
 * ```tsx
 * function ProgressIndicator() {
 *   const progress = useThreadEvent<{ step: number; total: number }>('progress')
 *
 *   if (!progress) return null
 *
 *   return (
 *     <div>
 *       Step {progress.step} of {progress.total}
 *     </div>
 *   )
 * }
 * ```
 *
 * @example
 * ```tsx
 * function StatusDisplay() {
 *   const status = useThreadEvent<{ message: string }>('status')
 *
 *   useEffect(() => {
 *     if (status) {
 *       console.log('Status updated:', status.message)
 *     }
 *   }, [status])
 *
 *   return <div>{status?.message ?? 'No status'}</div>
 * }
 * ```
 */
export function useThreadEvent<T = unknown>(type: string): T | null {
  // useThreadContextInternal throws if not within ThreadProvider
  const { subscribeToEvent } = useThreadContextInternal()
  const [eventData, setEventData] = useState<T | null>(null)

  useEffect(() => {
    // Subscribe to events of the specified type
    const unsubscribe = subscribeToEvent<T>(type, (data) => {
      setEventData(data)
    })

    // Cleanup subscription on unmount
    return unsubscribe
  }, [subscribeToEvent, type])

  return eventData
}
